// A record, full page: breadcrumb, actions, read-only status bar, field sheet, tabs (opening on
// the API's default_tab), and the activity timeline. Read-only, except merging an open PR from the
// Pull request tab (components/Merge.tsx; the API guards it).
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  api,
  errorMessage,
  type ActivityEvent,
  type Field,
  type FileDoc,
  type GitHubLink,
  type IssueSummary,
  type MergeResult,
  type PullSummary,
  type Role,
  type Record as RecordT,
  type Tab,
} from '../api/client';
import {
  BadgeTag,
  COLUMN_NAMES,
  COLUMN_ORDER,
  COLUMN_TONE,
  ChecksSummary,
  EpicTag,
  KIND_ICON,
  KIND_NAME,
  LabelTag,
  ReviewText,
  RunMark,
  StatusPill,
  prStateText,
  tagClass,
} from '../components/Bits';
import { Icon, type IconName } from '../components/Icon';
import { Markdown } from '../components/Markdown';
import { MergeArea } from '../components/Merge';
import { Shell } from '../components/Shell';
import { Button, Callout, Card, EmptyState, buttonClass, cn, mono } from '../components/ui';
import { boardPath } from '../lib/routes';
import { findWorkspace, useSession } from '../lib/session';
import { ago, clock, day } from '../lib/time';
import type { LabelRoles } from '../lib/labels';
import { useLoad } from '../lib/useLoad';
import { NotFoundPage } from './NotFoundPage';

// Hook classes (no styles; tests and the browser check select on them): sheet, sheet-id, pr-panel,
// name, raw, ev.
// On a phone the strip scrolls sideways inside itself; its right edge fades to say there is more.
const FADE_RIGHT = 'phone:[mask-image:linear-gradient(to_right,currentColor_calc(100%-var(--fade-edge)),transparent)]';
const STACK = 'grid min-w-0 gap-3';
const MUTED_SM = 'm-0 px-1 py-2 text-ink-muted';
const LIST = 'm-0 list-none rounded-lg border border-hairline p-0';
const LIST_ROW = 'flex min-w-0 flex-wrap items-center gap-2.5 border-t border-hairline px-3 py-2 text-[12.5px] first:border-t-0';
const FILE_LINE = 'flex flex-wrap items-center gap-2 font-mono text-[11.5px] text-ink-muted [overflow-wrap:anywhere]';
const FACT_K = 'mb-0.5 block text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-muted';
const FACT_V = 'inline-flex items-center gap-1.5 font-medium text-ink-display';
const RUN_TEXT = { pass: 'text-checks-pass-ink', fail: 'font-semibold text-checks-fail-ink', pending: 'text-checks-pending-ink' } as const;
const REF =
  'rounded-[5px] border border-hairline bg-surface px-1.5 py-px font-mono text-[11.5px] text-ink-display [overflow-wrap:anywhere]';
const CNT = 'font-mono text-[11px] text-ink-muted';
const REC = 'flex min-w-0 flex-1 flex-col bg-surface';
const BAR = 'flex min-w-0 items-center border-b border-hairline bg-page px-5 py-2.5 phone:px-3 phone:py-2';
const EV_TONE = {
  ok: 'border-checks-pass text-checks-pass-ink',
  bad: 'border-checks-fail text-checks-fail-ink',
  warn: 'border-checks-pending text-checks-pending-ink',
  '': 'border-hairline text-ink-muted',
} as const;

function Stepper({ column }: { column: RecordT['card']['column'] }) {
  const at = column ? COLUMN_ORDER.indexOf(column) : -1;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // On a narrow screen the strip scrolls: bring the current step into view.
    const wrap = ref.current;
    const step = wrap?.querySelector<HTMLElement>('[aria-current]');
    if (wrap && step && wrap.scrollWidth > wrap.clientWidth) wrap.scrollLeft = step.offsetLeft - wrap.clientWidth / 3;
  }, [column]);
  return (
    <div
      ref={ref}
      className={cn(
        'relative ml-auto min-w-0 max-w-full overflow-x-auto [scrollbar-width:none] phone:ml-0 [&::-webkit-scrollbar]:hidden',
        FADE_RIGHT,
      )}
      aria-label={`Status: ${column ? COLUMN_NAMES[column] : 'hidden'} (read-only)`}
      role="group"
    >
      <ol className="m-0 flex list-none items-center p-0">
        {COLUMN_ORDER.map((c, i) => (
          <li
            key={c}
            className={cn(
              '-ml-1.5 flex h-7 items-center gap-[5px] whitespace-nowrap pl-5 pr-3.5 text-[12px] [clip-path:polygon(0_0,calc(100%-9px)_0,100%_50%,calc(100%-9px)_100%,0_100%,9px_50%)] phone:h-[26px] phone:text-[11.5px]',
              'first:ml-0 first:rounded-l first:pl-3 first:[clip-path:polygon(0_0,calc(100%-9px)_0,100%_50%,calc(100%-9px)_100%,0_100%)]',
              i === at
                ? cn(COLUMN_TONE[c].pill, "font-semibold before:size-1.5 before:rounded-full before:bg-current before:content-['']")
                : 'bg-badge-hold-bg text-ink-display',
              i < at && '[&>svg]:text-checks-pass',
            )}
            aria-current={i === at ? 'step' : undefined}
          >
            {i < at && <Icon name="check" small />}
            {COLUMN_NAMES[c]}
          </li>
        ))}
      </ol>
    </div>
  );
}

function FieldValue({ f, roles }: { f: Field; roles: LabelRoles }) {
  if (Array.isArray(f.value)) {
    if (f.value.length === 0) return <span className="text-ink-muted">—</span>;
    if (f.name === 'labels') return <>{f.value.map((v) => <LabelTag key={String(v)} name={String(v)} roles={roles} />)}</>;
    return (
      <>
        {f.value.map((v) => (
          <span
            key={String(v)}
            className="inline-flex h-[22px] items-center gap-[5px] rounded-[6px] border border-hairline bg-surface px-2 font-mono text-[11.5px] text-ink-display"
          >
            {f.name === 'issues' ? `#${v}` : v}
          </span>
        ))}
      </>
    );
  }
  if (f.value === '' || f.value === null) return <span className="text-ink-muted">—</span>;
  const isMono = ['id', 'path', 'meta_repo', 'repo', 'created', 'shipped', 'branch'].includes(f.name);
  return <span className={isMono ? mono : undefined}>{String(f.value)}</span>;
}

function Frontmatter({ fm }: { fm: Record<string, unknown> }) {
  const lines = Object.entries(fm).map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`);
  if (!lines.length) return null;
  return (
    <pre
      className="m-0 whitespace-pre-wrap border-b border-dashed border-hairline pb-2.5 font-mono text-[12px] leading-[1.6] [overflow-wrap:anywhere]"
      aria-label="Frontmatter"
    >
      {lines.join('\n')}
    </pre>
  );
}

interface MergeProps {
  repo: number;
  role: Role;
  github: GitHubLink;
  onMerged: (result: MergeResult) => void;
}

function PrPanel({ pr, merge }: { pr: PullSummary; merge: MergeProps }) {
  return (
    <Card as="article" padding="none" className="pr-panel overflow-hidden" data-pr={pr.number}>
      <div className="grid gap-1.5 border-b border-hairline px-3.5 py-3">
        <div className="text-[13.5px] font-semibold text-ink-display [overflow-wrap:anywhere]">
          <span className={cn(mono, 'font-medium text-ink-muted')}>#{pr.number}</span> {pr.title}
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[12px] text-ink-muted">
          <Icon name="branch" small />
          <span className={REF}>{pr.head}</span>
          <Icon name="arrow" small />
          <span className={REF}>{pr.base}</span>
          <span>
            · {pr.repo}
            {pr.author ? ` · ${pr.author}` : ''}
            {pr.updated_at ? ` · updated ${ago(pr.updated_at)}` : ''}
          </span>
          {pr.release && <BadgeTag badge="release" />}
          {pr.draft && <BadgeTag badge="draft" />}
        </div>
      </div>
      <div className="border-t border-hairline px-3.5 py-2 text-[12px] text-ink-muted" aria-label="Production deployment">
        <span className="font-medium text-ink-display">Production: </span>
        {pr.production.deployed ? 'Deployed in the active production version.' : pr.production.reported
          ? `Not in the active production version. Latest attempt: ${pr.production.attempt_state ?? 'reported'}.`
          : 'No deployment reported.'}
      </div>
      {pr.checks.runs.length > 0 ? (
        <ul className="m-0 list-none py-1" aria-label="Checks">
          {pr.checks.runs.map((r, i) => (
            <li key={`${r.name}-${i}`} data-run={r.state} className="flex flex-wrap items-center gap-2.5 px-3.5 py-1.5 text-[12.5px]">
              <RunMark state={r.state} />
              <span className="name font-mono text-[12px] text-ink-display [overflow-wrap:anywhere]">{r.name}</span>
              <span className={RUN_TEXT[r.state]}>{r.state === 'pass' ? 'passed' : r.state === 'fail' ? 'failed' : 'pending'}</span>
              {r.url && (
                <a className="ml-auto text-[11.5px]" href={r.url} target="_blank" rel="noopener noreferrer">
                  details
                </a>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className={MUTED_SM}>No checks reported on the head commit.</p>
      )}
      <div className="grid grid-cols-3 border-t border-hairline phone:grid-cols-[minmax(0,1fr)] [&>div]:min-w-0 [&>div]:px-3.5 [&>div]:py-[9px] [&>div]:text-[12px] [&>div+div]:border-l [&>div+div]:border-hairline phone:[&>div+div]:border-l-0 phone:[&>div+div]:border-t">
        <div>
          <span className={FACT_K}>Checks</span>
          <span className={FACT_V}>
            <ChecksSummary checks={pr.checks} />
          </span>
        </div>
        <div>
          <span className={FACT_K}>Review</span>
          <span className={FACT_V}>
            <ReviewText review={pr.review} />
          </span>
        </div>
        <div>
          <span className={FACT_K}>State</span>
          <span className={FACT_V}>
            {prStateText(pr)}
            {pr.merged_at ? ` ${day(pr.merged_at)}` : ''}
          </span>
        </div>
      </div>
      {pr.state === 'open' && <MergeArea pr={pr} {...merge} />}
      <div className="flex flex-wrap items-center gap-2.5 border-t border-hairline bg-surface px-3.5 py-3">
        <a className={buttonClass()} href={pr.url} target="_blank" rel="noopener noreferrer">
          <Icon name="gh" />
          Open on GitHub
          <Icon name="ext" small />
        </a>
      </div>
    </Card>
  );
}

function IssueList({ items, roles }: { items: IssueSummary[]; roles: LabelRoles }) {
  return (
    <ul className={LIST}>
      {items.map((i) => (
        <li key={i.number} className={LIST_ROW}>
          <span className="font-mono text-[11.5px] text-ink-muted">#{i.number}</span>
          <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
            <a href={i.url} target="_blank" rel="noopener noreferrer">
              {i.title}
            </a>
          </span>
          {i.labels.map((l) => (
            <LabelTag key={l} name={l} roles={roles} />
          ))}
          <span className="text-[11.5px] text-ink-muted">{i.state}</span>
        </li>
      ))}
    </ul>
  );
}

function ResearchTab({ repo, tab }: { repo: number; tab: Tab }) {
  const [open, setOpen] = useState<string | null>(null);
  const [doc, setDoc] = useState<{ path: string; doc?: FileDoc; error?: string } | null>(null);
  useEffect(() => {
    if (!open) return;
    let live = true;
    api
      .GET('/api/repos/{repo_id}/files', { params: { path: { repo_id: repo }, query: { path: open } } })
      .then(({ data, error, response }) => {
        if (!live) return;
        setDoc(data ? { path: open, doc: data } : { path: open, error: errorMessage(error, response.status) });
      })
      .catch(() => live && setDoc({ path: open, error: errorMessage(null) }));
    return () => {
      live = false;
    };
  }, [open, repo]);
  return (
    <div className="grid gap-4">
      <ul className={LIST} aria-label="Research files">
        {tab.files.map((f) => (
          <li key={f.path} className={LIST_ROW}>
            <Icon name="file" small />
            <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
              <button
                type="button"
                className="cursor-pointer border-0 bg-transparent p-0 text-left text-accent-display aria-pressed:font-semibold"
                aria-pressed={open === f.path}
                onClick={() => setOpen(f.path)}
              >
                {f.path.split('/').pop()}
              </button>
            </span>
            <a className="text-ink-muted" href={f.html_url} target="_blank" rel="noopener noreferrer" aria-label={`${f.path} on GitHub`}>
              <Icon name="ext" small />
            </a>
          </li>
        ))}
      </ul>
      {open && (
        <div className="grid min-w-0 gap-2.5">
          {!doc || doc.path !== open ? (
            <p className="text-ink-muted">Loading {open}…</p>
          ) : doc.error ? (
            <Callout as="p" role="alert">
              {doc.error}
            </Callout>
          ) : doc.doc ? (
            <>
              <div className={FILE_LINE}>
                <span>{doc.doc.path}</span>
                <a
                  className="ml-auto inline-flex items-center gap-1 font-sans text-[12px]"
                  href={doc.doc.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub <Icon name="ext" small />
                </a>
              </div>
              {doc.doc.error && <Callout as="p">{doc.doc.error}</Callout>}
              {doc.doc.frontmatter && <Frontmatter fm={doc.doc.frontmatter} />}
              <Markdown source={doc.doc.markdown} />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

function TabPanel({ rec, tab, repo, merge }: { rec: RecordT; tab: Tab; repo: number; merge: MergeProps }) {
  if (!tab.available) {
    return (
      <EmptyState title={tab.path ?? tab.label} className="[&_b]:font-mono [&_b]:text-[12.5px]">
        {tab.placeholder ?? 'not written yet'}
      </EmptyState>
    );
  }
  if (tab.key === 'pull_request') {
    const prs = tab.items as PullSummary[];
    return (
      <div className={STACK}>
        {prs.map((p) => (
          <PrPanel key={`${p.repo}#${p.number}`} pr={p} merge={merge} />
        ))}
      </div>
    );
  }
  if (tab.key === 'research') return <ResearchTab repo={repo} tab={tab} />;
  if (tab.key === 'file') {
    return (
      <div className={STACK}>
        {rec.card.error && (
          <Callout as="p" tone="danger" outline role="note" className="items-center">
            <Icon name="warn" small /> Not parsed: {rec.card.error}
          </Callout>
        )}
        {tab.path && <div className={FILE_LINE}>{tab.path}</div>}
        <pre className="raw m-0 whitespace-pre-wrap rounded border border-hairline p-3 font-mono text-[12px] [overflow-wrap:anywhere]">
          {tab.markdown}
        </pre>
      </div>
    );
  }
  const issues = tab.key === 'issues' || tab.key === 'issue' ? (tab.items as IssueSummary[]) : [];
  return (
    <div className={STACK}>
      {tab.path && (
        <div className={FILE_LINE}>
          <span>{tab.path}</span>
        </div>
      )}
      {issues.length > 0 && <IssueList items={issues} roles={rec.labels} />}
      {tab.frontmatter && <Frontmatter fm={tab.frontmatter} />}
      {tab.markdown ? <Markdown source={tab.markdown} /> : null}
    </div>
  );
}

const EVENT_ICON: Record<ActivityEvent['kind'], IconName> = {
  commit: 'commit',
  label: 'tag',
  issue: 'bug',
  pr: 'pr',
  review: 'eye',
  check: 'check',
  merge: 'merge',
};

function Activity({ repo, recordKey }: { repo: number; recordKey: string }) {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [message, setMessage] = useState('');

  async function load(cursor: string | null) {
    setState('loading');
    try {
      const { data, error, response } = await api.GET('/api/repos/{repo_id}/records/{key}/activity', {
        params: { path: { repo_id: repo, key: recordKey }, query: { limit: 20, ...(cursor ? { cursor } : {}) } },
      });
      if (data) {
        setEvents((prev) => (cursor ? [...prev, ...data.events] : data.events));
        setErrors(data.errors);
        setNext(data.next_cursor);
        setState('ok');
      } else {
        setMessage(errorMessage(error, response.status));
        setState('error');
      }
    } catch {
      setMessage(errorMessage(null));
      setState('error');
    }
  }
  useEffect(() => {
    setEvents([]);
    void load(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo, recordKey]);

  let lastDay = '';
  return (
    <aside className="sticky top-[70px] min-w-0 compact:static" aria-labelledby="activity-h">
      <div className="flex items-baseline gap-2 border-b border-hairline px-1 pb-3 pt-0.5">
        <h2 id="activity-h" className="m-0 text-[13px] font-semibold">
          Activity
        </h2>
        <span className="text-[11.5px] text-ink-muted">git and GitHub · read-only</span>
      </div>
      {errors.length > 0 && (
        <Callout tone="warning" role="note" icon={<Icon name="warn" small />} className="mt-2.5 text-[12px]">
          Some activity could not be read just now: {errors.join('; ')}
        </Callout>
      )}
      <ol className="m-0 list-none p-0 pt-1.5">
        {events.map((e, i) => {
          const d = day(e.at);
          const head = d !== lastDay;
          lastDay = d;
          const tone = e.state === 'fail' ? 'bad' : e.state === 'pass' ? 'ok' : e.state === 'pending' ? 'warn' : '';
          return (
            <li key={`${e.at}-${i}`}>
              {head && (
                <div className="flex items-center gap-2 px-1 pb-1.5 pt-3.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-muted after:h-px after:flex-1 after:bg-hairline after:content-['']">
                  {d}
                </div>
              )}
              <div className="ev grid grid-cols-[26px_minmax(0,1fr)] gap-2.5 px-1 py-[7px]" data-kind={e.kind}>
                <span className={cn('grid size-[26px] place-items-center rounded-full border', EV_TONE[tone])} aria-hidden="true">
                  <Icon name={e.state === 'fail' ? 'x' : EVENT_ICON[e.kind]} small />
                </span>
                <span className="min-w-0 text-[12.5px] leading-[1.45] [overflow-wrap:anywhere]">
                  {e.url ? (
                    <a className="text-ink-display" href={e.url} target="_blank" rel="noopener noreferrer">
                      {e.summary}
                    </a>
                  ) : (
                    e.summary
                  )}
                  {e.state === 'fail' && <span className="font-semibold text-checks-fail-ink"> (failed)</span>}
                  <span className="mt-px block text-[11.5px] text-ink-muted">
                    {[
                      e.kind === 'merge' && e.actor ? `merged by ${e.actor}` : e.actor,
                      clock(e.at),
                      e.sha ? e.sha.slice(0, 7) : null,
                      e.repo,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </div>
            </li>
          );
        })}
      </ol>
      {state === 'loading' && <p className={MUTED_SM}>Loading activity…</p>}
      {state === 'error' && (
        <Callout as="p" role="alert">
          {message}
        </Callout>
      )}
      {state === 'ok' && events.length === 0 && <p className={MUTED_SM}>No activity yet.</p>}
      {next && state !== 'loading' && (
        <Button className="mt-2 w-full justify-center" onClick={() => void load(next)}>
          Load older activity
        </Button>
      )}
      <p className="mt-2 border-t border-hairline px-1 pt-3 text-[11.5px] text-ink-muted">
        Comments live on GitHub; the board never writes to the repo.
      </p>
    </aside>
  );
}

const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export function RecordPage() {
  const params = useParams();
  const ws = Number(params.ws);
  const repo = Number(params.repo);
  const key = params.key ?? '';
  const { me } = useSession();
  const workspace = findWorkspace(me, ws);
  const [search, setSearch] = useSearchParams();
  const [merged, setMerged] = useState<MergeResult | null>(null);
  const [rec, reload] = useLoad(
    () => api.GET('/api/repos/{repo_id}/records/{key}', { params: { path: { repo_id: repo, key } } }),
    `${repo}:${key}`,
  );
  const [copied, setCopied] = useState(false);

  if (!workspace || !Number.isInteger(repo)) return <NotFoundPage />;
  const mergeProps: MergeProps = {
    repo,
    role: workspace.role,
    github: me.github,
    onMerged: (result) => {
      setMerged(result);
      // Stay on the Pull request tab: after the merge the API's default tab changes.
      const next = new URLSearchParams(search);
      next.set('tab', 'pull_request');
      setSearch(next, { replace: true });
      reload();
    },
  };
  const mergedNote = merged && (
    <Callout
      as="p"
      tone="success"
      outline
      role="status"
      className="mx-6 mb-0 mt-3 flex-wrap items-center gap-1 text-[12.5px] phone:mx-3 phone:mt-2.5"
    >
      <Icon name="merge" small /> Merged <span className={mono}>#{merged.pull?.number}</span> into{' '}
      <span className={mono}>{merged.pull?.base}</span>
      {merged.sha ? (
        <>
          {' '}
          as <span className={mono}>{merged.sha.slice(0, 7)}</span>
        </>
      ) : null}{' '}
      (@{merged.login}). The card shows <b>Merged — needs report</b> until the report is filed.
    </Callout>
  );
  let body;
  if (rec.state === 'loading') body = <p className="px-5 py-4 text-ink-muted">Loading {key}…</p>;
  else if (rec.state === 'error' && rec.status === 404 && merged)
    // An unlinked PR's card leaves the board once merged.
    body = (
      <div className={REC}>
        {mergedNote}
        <p className="px-5 py-4">
          <Link to={boardPath(ws)} className={buttonClass()}>
            <Icon name="left" small />
            Board
          </Link>
        </p>
      </div>
    );
  else if (rec.state === 'error')
    body =
      rec.status === 404 ? (
        <NotFoundPage bare />
      ) : (
        <Callout as="p" role="alert">
          {rec.message}
        </Callout>
      );
  else {
    const r = rec.data;
    const card = r.card;
    const asked = search.get('tab');
    const current = r.tabs.find((t) => t.key === asked && t.available)?.key ?? r.default_tab ?? r.tabs[0]?.key;
    const tab = r.tabs.find((t) => t.key === current);
    const openPr = card.prs.find((p) => p.state === 'open');
    const crumbs = r.breadcrumb;
    const missing = r.tabs.filter((t) => !t.available);
    body = (
      <div className={REC}>
        {mergedNote}
        <nav className={cn(BAR, 'gap-2.5')} aria-label="Breadcrumb">
          <ol className="m-0 flex min-w-0 list-none flex-wrap items-center gap-1.5 p-0 text-[13px]">
            {crumbs.map((b) => (
              <li
                key={`${b.kind}-${b.id}`}
                className="inline-flex min-w-0 items-center gap-1.5 [overflow-wrap:anywhere] not-first:before:text-ink-muted not-first:before:content-['/']"
              >
                {b.kind === 'workspace' ? (
                  <Link to={boardPath(ws)}>{b.label}</Link>
                ) : b.kind === 'meta_repo' ? (
                  <Link to={boardPath(ws, `repo=${b.id}`)}>{b.label}</Link>
                ) : (
                  <span aria-current="page" className="font-mono text-[12.5px] font-semibold text-ink-display">
                    {b.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
        <div className={cn(BAR, 'flex-wrap gap-x-4 gap-y-3 phone:flex-col phone:items-stretch phone:gap-2')}>
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Link to={boardPath(ws)} className={buttonClass()}>
              <Icon name="left" small />
              Board
            </Link>
            {card.links.github && (
              <a className={buttonClass()} href={card.links.github} target="_blank" rel="noopener noreferrer">
                <Icon name="gh" />
                GitHub
              </a>
            )}
            <Button
              onClick={() => {
                void navigator.clipboard?.writeText(card.id).then(() => setCopied(true));
              }}
            >
              <Icon name="copy" />
              {copied ? 'Copied' : 'Copy ID'}
            </Button>
            {openPr && (
              <span className="inline-flex flex-wrap items-center gap-[5px] text-[11.5px] text-ink-muted">
                <Icon name="pr" small />#{openPr.number}: <ChecksSummary checks={openPr.checks} /> ·{' '}
                <ReviewText review={openPr.review} />
              </span>
            )}
          </div>
          <Stepper column={card.column} />
        </div>
        <div className="grid grid-cols-[minmax(0,1100px)_320px] items-start justify-center gap-6 px-5 pb-14 pt-6 compact:grid-cols-[minmax(0,1fr)] phone:gap-4 phone:px-3 phone:pb-8 phone:pt-3">
          <article className="sheet min-w-0 rounded-lg border border-hairline bg-page px-8 pb-8 pt-[26px] phone:p-4">
            <div className="sheet-id flex flex-wrap items-center gap-2 font-mono text-[12.5px] font-medium text-ink-muted">
              <Icon name={KIND_ICON[card.kind]} small />
              <span>{card.id}</span>
              <span className={tagClass('kind')}>{KIND_NAME[card.kind]}</span>
              {card.epic && <EpicTag epic={card.epic} />}
              {card.badges.map((b) => (
                <BadgeTag key={b} badge={b} />
              ))}
            </div>
            <h1 className="mb-[22px] mt-1.5 text-[24px] font-semibold leading-[1.25] tracking-[-0.025em] text-balance [overflow-wrap:anywhere] phone:mb-3.5 phone:text-[19px]">
              {card.title}
            </h1>
            {card.warnings.length > 0 && (
              <Callout tone="warning" className="mb-4">
                <ul className="m-0 grid list-none p-0" aria-label="Warnings">
                  {card.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </Callout>
            )}
            <dl className="m-0 grid grid-cols-2 gap-x-10 phone:grid-cols-[minmax(0,1fr)]">
              {r.fields.map((f) => (
                <div
                  key={f.name}
                  className="grid grid-cols-[120px_minmax(0,1fr)] items-baseline gap-3 border-b border-hairline py-2 text-[13px] phone:grid-cols-[96px_minmax(0,1fr)] phone:text-[12.5px]"
                >
                  <dt className="text-[12.5px] text-ink-muted">{f.label}</dt>
                  <dd className="m-0 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-ink-display [overflow-wrap:anywhere]">
                    {f.name === 'column' ? <StatusPill column={card.column} /> : <FieldValue f={f} roles={r.labels} />}
                  </dd>
                </div>
              ))}
            </dl>
            <div className="mt-[30px] phone:mx-[-16px] phone:mt-5">
              <div
                className="flex gap-0.5 overflow-x-auto border-b border-hairline pt-[3px] [scrollbar-width:thin] phone:px-2 phone:[scrollbar-width:none] phone:[&::-webkit-scrollbar]:hidden"
                role="tablist"
                aria-label="Record"
              >
                {r.tabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    id={`tab-${t.key}`}
                    aria-controls="tab-panel"
                    aria-selected={t.key === current}
                    disabled={!t.available}
                    title={t.available ? undefined : (t.placeholder ?? 'not written yet')}
                    className={cn(
                      // relative: holds its sr-only text, which otherwise escapes the strip's own scroll
                      // and widens the page on a phone (seen at 390px with Linux fonts).
                      'relative -mb-px inline-flex min-h-10 items-center gap-1.5 whitespace-nowrap rounded-t-[6px] border-0 border-b-2 bg-transparent px-3 pb-2.5 pt-[9px] text-[13px] phone:min-h-11',
                      !t.available
                        ? 'cursor-not-allowed border-transparent font-normal text-ink-muted'
                        : t.key === current
                          ? 'cursor-pointer border-accent font-semibold text-ink-display hover:bg-surface'
                          : 'cursor-pointer border-transparent font-medium text-ink-body hover:bg-surface',
                    )}
                    onClick={() => {
                      const next = new URLSearchParams(search);
                      if (t.key === r.default_tab) next.delete('tab');
                      else next.set('tab', t.key);
                      setSearch(next, { replace: true });
                    }}
                  >
                    {!t.available && (
                      <span className="size-1.5 rounded-full shadow-[inset_0_0_0_1.3px_var(--ink-muted)]" aria-hidden="true" />
                    )}
                    {t.label}
                    {!t.available && <span className="sr-only"> ({t.placeholder ?? 'not written yet'})</span>}
                    {t.key === 'research' && t.files.length > 0 && <span className={CNT}>{t.files.length}</span>}
                    {t.key === 'pull_request' && <span className={CNT}>{t.items.length}</span>}
                  </button>
                ))}
              </div>
              {missing.length > 0 && (
                <p className="mb-0 mt-2 text-[11.5px] text-ink-muted phone:px-4">
                  {capitalise(missing[0]?.placeholder ?? 'not written yet')}: {missing.map((t) => t.label).join(', ')}
                </p>
              )}
              <div
                className="min-w-0 pt-[18px] phone:px-4 phone:pt-4"
                role="tabpanel"
                id="tab-panel"
                aria-labelledby={current ? `tab-${current}` : undefined}
              >
                {tab ? <TabPanel rec={r} tab={tab} repo={repo} merge={mergeProps} /> : <p className="text-ink-muted">Nothing to show.</p>}
              </div>
            </div>
          </article>
          <Activity repo={repo} recordKey={key} />
        </div>
      </div>
    );
  }
  return <Shell ws={ws}>{body}</Shell>;
}
